<script setup lang="ts">
import { computed, ref } from 'vue'
import { RouterLink } from 'vue-router'
import {
  ArrowRight,
  Boxes,
  Clock3,
  Code2,
  ExternalLink,
  GalleryHorizontalEnd,
  PlayCircle,
  Smartphone,
  Users,
  Video,
} from 'lucide-vue-next'
import DemoVideo from '@/components/DemoVideo.vue'
import MiniProgramDialog from '@/components/MiniProgramDialog.vue'
import VideoHomeHeader from '@/components/VideoHomeHeader.vue'
import VideoLightbox from '@/components/VideoLightbox.vue'
import { NOVA_AI_URL } from '@/config/external-links'
import {
  courseVideos,
  featuredVideos,
  heroVideo,
  verticalVideos,
  type DemoVideo as DemoVideoRecord,
} from '@/data/videoDemo'
import { translateDemoVideo } from '@/data/videoDemoEnglish'
import { getVideoHomeCopy, type HomeLocale } from '@/data/videoHomeCopy'

const props = withDefaults(defineProps<{ locale?: HomeLocale }>(), { locale: 'zh' })
const activeVideo = ref<DemoVideoRecord | null>(null)
const miniProgramOpen = ref(false)
const english = computed(() => props.locale === 'en')
const worksPath = computed(() => english.value ? { path: '/en', hash: '#video-cases' } : '/works')
const coursesPath = computed(() => english.value ? { path: '/en', hash: '#video-courses' } : '/courses')
const copy = computed(() => getVideoHomeCopy(props.locale).page)
const displayHeroVideo = computed(() => translateDemoVideo(heroVideo, english.value))
const displayFeaturedVideos = computed(() => featuredVideos.map(video => translateDemoVideo(video, english.value)))
const displayCourseVideos = computed(() => courseVideos.map(video => translateDemoVideo(video, english.value)))
const displayVerticalVideos = computed(() => verticalVideos.map(video => translateDemoVideo(video, english.value)))
const creationDemo = computed<DemoVideoRecord>(() => displayFeaturedVideos.value[0]!)
const openVideo = (video: DemoVideoRecord) => { activeVideo.value = video }
</script>

<template>
  <div class="video-home-page" :class="{ english }">
    <VideoHomeHeader :locale="props.locale" />

    <main>
      <section class="video-hero" aria-labelledby="video-hero-title">
        <DemoVideo :video="displayHeroVideo" :locale="props.locale" mode="hero" autoplay loop />
        <div class="video-hero-mask"></div>
        <div class="video-hero-content">
          <h1 id="video-hero-title">Nova</h1>
          <p>{{ copy.heroTagline }}</p>
          <div class="video-hero-actions">
            <a class="video-button primary" :href="NOVA_AI_URL" target="_blank" rel="noopener">
              <Video :size="18" aria-hidden="true" />{{ copy.startCreating }}
            </a>
            <a class="video-button ghost" href="#video-cases">
              <GalleryHorizontalEnd :size="18" aria-hidden="true" />{{ copy.exploreVideos }}
            </a>
          </div>
        </div>
      </section>

      <section id="video-cases" class="video-section cases-section">
        <div class="video-shell video-section-heading">
          <div>
            <h2>{{ copy.featuredTitle }}</h2>
            <p>{{ copy.featuredDescription }}</p>
          </div>
          <RouterLink :to="worksPath">{{ copy.viewWorks }} <ArrowRight :size="17" aria-hidden="true" /></RouterLink>
        </div>

        <div class="case-rail">
          <article
            v-for="(videoItem, index) in displayFeaturedVideos"
            :key="videoItem.id"
            class="video-case-card"
            :class="{ featured: index === 0, square: videoItem.ratio === '1:1' }"
            @click="openVideo(videoItem)"
          >
            <div class="case-video-frame">
              <DemoVideo :video="videoItem" :locale="props.locale" mode="hover" :preview-limit="8" />
            </div>
            <div class="case-card-copy">
              <div><span>{{ videoItem.type }}</span><span>{{ videoItem.context }}</span></div>
              <h3>{{ videoItem.title }}</h3>
              <div class="video-meta"><Clock3 :size="15" aria-hidden="true" />{{ videoItem.duration }}<span>{{ videoItem.ratio }}</span></div>
              <div class="case-actions">
                <button type="button" @click.stop="openVideo(videoItem)"><PlayCircle :size="17" aria-hidden="true" />{{ copy.viewDetails }}</button>
                <a :href="NOVA_AI_URL" target="_blank" rel="noopener" @click.stop>{{ copy.tryNow }} <ExternalLink :size="15" aria-hidden="true" /></a>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section class="video-create-section">
        <div class="video-shell create-layout">
          <div class="create-copy">
            <h2>{{ copy.creationTitle }}</h2>
            <p>{{ copy.creationDescription }}</p>
            <a class="video-button primary" :href="NOVA_AI_URL" target="_blank" rel="noopener">
              <Video :size="18" aria-hidden="true" />{{ copy.enterPlatform }}
            </a>
          </div>
          <button class="create-preview" type="button" @click="openVideo(creationDemo)">
            <img :src="creationDemo.poster" :alt="copy.generatedCaseAlt" />
            <span><PlayCircle :size="28" aria-hidden="true" />{{ copy.playGeneratedCase }}</span>
          </button>
        </div>
      </section>

      <section id="video-courses" class="video-section course-preview-section">
        <div class="video-shell video-section-heading">
          <div>
            <h2>{{ copy.coursesTitle }}</h2>
            <p>{{ copy.coursesDescription }}</p>
          </div>
          <RouterLink :to="coursesPath">{{ copy.enterLearningCenter }} <ArrowRight :size="17" aria-hidden="true" /></RouterLink>
        </div>

        <div class="video-shell course-preview-grid">
          <article v-for="videoItem in displayCourseVideos" :key="videoItem.id" class="course-preview-card">
            <div class="course-video-frame">
              <DemoVideo :video="videoItem" :locale="props.locale" mode="click" @open="openVideo" />
            </div>
            <div class="course-preview-copy">
              <span>{{ videoItem.type }}</span>
              <h3>{{ videoItem.title }}</h3>
              <p>{{ videoItem.context }}{{ english ? '. ' : '。' }}{{ copy.courseDescriptionSupplement }}</p>
              <div class="course-meta">
                <span><Clock3 :size="15" aria-hidden="true" />{{ videoItem.duration }}</span>
                <span><Users :size="15" aria-hidden="true" />{{ copy.courseFormat }}</span>
              </div>
              <div class="course-actions">
                <button type="button" @click="openVideo(videoItem)"><PlayCircle :size="17" aria-hidden="true" />{{ copy.freePreview }}</button>
                <RouterLink :to="coursesPath">{{ copy.enterCourse }} <ArrowRight :size="16" aria-hidden="true" /></RouterLink>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section id="creators" class="video-section vertical-section">
        <div class="video-shell video-section-heading light-heading">
          <div>
            <h2>{{ copy.verticalTitle }}</h2>
            <p>{{ copy.verticalDescription }}</p>
          </div>
        </div>

        <div class="vertical-video-rail">
          <article
            v-for="videoItem in displayVerticalVideos"
            :key="videoItem.id"
            class="vertical-video-card"
            :class="{ square: videoItem.ratio === '1:1' }"
            @click="openVideo(videoItem)"
          >
            <DemoVideo :video="videoItem" :locale="props.locale" mode="hover" :preview-limit="8" />
            <div class="vertical-video-copy"><span>{{ videoItem.type }}</span><strong>{{ videoItem.title }}</strong><small>{{ videoItem.duration }} · {{ videoItem.ratio }}</small></div>
          </article>
        </div>
      </section>

      <section id="business" class="products-section">
        <div class="video-shell">
          <div class="product-cta">
            <div>
              <h2>{{ copy.productTitle }}</h2>
              <p>{{ copy.productDescription }}</p>
            </div>
            <a class="video-button primary" :href="NOVA_AI_URL" target="_blank" rel="noopener">{{ copy.openGenerator }} <ExternalLink :size="17" aria-hidden="true" /></a>
          </div>
          <div class="product-entry-grid">
            <a :href="NOVA_AI_URL" target="_blank" rel="noopener"><Video :size="24" aria-hidden="true" /><span><strong>{{ copy.aiStudio }}</strong><small>{{ copy.aiStudioStatus }}</small></span><ExternalLink :size="16" aria-hidden="true" /></a>
            <div><Boxes :size="24" aria-hidden="true" /><span><strong>{{ copy.yingliu }}</strong><small>{{ copy.yingliuStatus }}</small></span></div>
            <button class="mini-program-entry" type="button" @click="miniProgramOpen = true"><Smartphone :size="24" aria-hidden="true" /><span><strong>{{ copy.miniProgram }}</strong><small>{{ copy.miniProgramStatus }}</small></span><ArrowRight :size="16" aria-hidden="true" /></button>
            <div><Code2 :size="24" aria-hidden="true" /><span><strong>{{ copy.openPlatform }}</strong><small>{{ copy.openPlatformStatus }}</small></span></div>
          </div>
        </div>
      </section>
    </main>

    <footer class="video-home-footer">
      <div class="video-shell">
        <span>{{ copy.footerTitle }}</span>
        <span>{{ copy.footerNote }}</span>
      </div>
    </footer>

    <VideoLightbox :video="activeVideo" :locale="props.locale" @close="activeVideo = null" />
    <MiniProgramDialog :open="miniProgramOpen" :locale="props.locale" @close="miniProgramOpen = false" />
  </div>
</template>

<style scoped>
.video-home-page {
  --video-ink: #0a0d11;
  --video-paper: #f6f7f5;
  --video-muted: #70777e;
  --video-line: rgba(10,13,17,.13);
  --video-cyan: #62dbea;
  --video-shell: min(1580px, calc(100% - 96px));
  min-height: 100vh;
  color: var(--video-ink);
  background: var(--video-paper);
}
.video-shell { width: var(--video-shell); margin-inline: auto; }
.video-hero { position: relative; min-height: 100svh; overflow: hidden; color: #fff; background: #1d3442; }
.video-hero > :deep(.demo-video) { position: absolute; inset: 0; border-radius: 0; }
.video-hero-mask { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(2,8,13,.46), rgba(2,8,13,.04) 34%, rgba(2,8,13,.18) 62%, rgba(2,8,13,.78)), linear-gradient(90deg, rgba(3,9,14,.26), transparent 55%); pointer-events: none; }
.video-hero-content { position: relative; z-index: 3; min-height: 100svh; padding: 20svh 24px 170px; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; pointer-events: none; }
.video-hero-content h1 { margin: 0; font-family: "Iowan Old Style", Baskerville, "Times New Roman", serif; font-size: clamp(116px, 16vw, 300px); font-weight: 500; font-style: italic; line-height: .78; letter-spacing: -.085em; text-shadow: 0 18px 70px rgba(0,0,0,.2); }
.video-hero-content p { margin: 30px 0 28px; font-size: 18px; letter-spacing: .12em; }
.video-hero-actions { display: flex; flex-wrap: wrap; justify-content: center; gap: 11px; }
.video-hero-actions a { pointer-events: auto; }
.video-button { min-height: 48px; padding: 0 24px; display: inline-flex; align-items: center; justify-content: center; gap: 9px; border: 1px solid transparent; border-radius: 999px; font-weight: 700; transition: transform .2s ease, background .2s ease; }
.video-button:hover { transform: translateY(-2px); }
.video-button.primary { color: #071b22; background: var(--video-cyan); }
.video-button.ghost { color: #fff; border-color: rgba(255,255,255,.58); background: rgba(0,0,0,.16); backdrop-filter: blur(10px); }
.video-section { padding: 155px 0; }
.video-section-heading { margin-bottom: 52px; display: flex; align-items: end; justify-content: space-between; gap: 50px; }
.video-section-heading h2, .create-copy h2, .product-cta h2 { margin: 0; font-family: "Songti SC", STSong, serif; font-size: clamp(56px, 6.5vw, 104px); font-weight: 500; line-height: 1; letter-spacing: -.055em; }
.english .video-section-heading h2,
.english .create-copy h2,
.english .product-cta h2 { font-family: "Iowan Old Style", Baskerville, "Times New Roman", serif; line-height: .94; letter-spacing: -.045em; }
.english .video-hero-content p { max-width: 680px; letter-spacing: .025em; }
.video-section-heading p { margin: 16px 0 0; color: var(--video-muted); font-size: 14px; }
.video-section-heading > a { display: inline-flex; align-items: center; gap: 7px; padding-bottom: 4px; color: var(--video-muted); border-bottom: 1px solid currentColor; font-size: 13px; white-space: nowrap; }
.cases-section { padding-bottom: 140px; background: #fff; }
.case-rail { padding-left: max(48px, calc((100vw - min(1580px, calc(100vw - 96px))) / 2)); display: flex; gap: 12px; overflow-x: auto; overscroll-behavior-inline: contain; scrollbar-width: none; }
.case-rail::-webkit-scrollbar { display: none; }
.video-case-card { flex: 0 0 42vw; min-width: 540px; overflow: hidden; border-radius: 20px; color: #fff; background: #0d1319; cursor: pointer; }
.video-case-card.featured { flex-basis: 52vw; }
.case-video-frame { height: min(500px, 32vw); min-height: 390px; }
.case-video-frame :deep(.demo-video) { border-radius: 0; }
.case-card-copy { padding: 24px 25px 25px; }
.case-card-copy > div:first-child { display: flex; gap: 9px; color: rgba(255,255,255,.5); font-size: 11px; }
.case-card-copy h3 { margin: 12px 0; font-size: 24px; }
.video-meta { display: flex; align-items: center; gap: 6px; color: rgba(255,255,255,.56); font-size: 12px; }
.video-meta span { margin-left: 7px; }
.case-actions, .course-actions { margin-top: 20px; display: flex; align-items: center; gap: 12px; }
.case-actions button, .case-actions a, .course-actions button, .course-actions a { min-height: 40px; padding: 0 15px; display: inline-flex; align-items: center; gap: 7px; border-radius: 999px; font-weight: 700; font-size: 12px; }
.case-actions button { color: #071b22; background: var(--video-cyan); }
.case-actions a { color: #fff; border: 1px solid rgba(255,255,255,.2); }
.video-create-section { padding: 145px 0; color: #fff; background: #070c11; }
.create-layout { display: grid; grid-template-columns: .9fr 1.1fr; align-items: center; gap: 80px; }
.create-copy h2 { color: #fff; }
.create-copy p { max-width: 560px; margin: 28px 0 32px; color: rgba(255,255,255,.58); font-size: 16px; line-height: 1.85; }
.create-preview { position: relative; height: 560px; padding: 0; overflow: hidden; color: #fff; border: 0; border-radius: 22px; background: #111820; }
.create-preview::after { content: ""; position: absolute; inset: 0; background: linear-gradient(180deg, transparent 50%, rgba(1,5,10,.76)); }
.create-preview img { width: 100%; height: 100%; object-fit: cover; transition: transform .5s ease; }
.create-preview:hover img { transform: scale(1.035); }
.create-preview span { position: absolute; z-index: 2; left: 25px; bottom: 22px; display: inline-flex; align-items: center; gap: 10px; font-size: 16px; font-weight: 700; }
.course-preview-section { background: var(--video-paper); }
.course-preview-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.course-preview-card { overflow: hidden; border: 1px solid var(--video-line); border-radius: 20px; background: #fff; }
.course-video-frame { height: 360px; }
.course-video-frame :deep(.demo-video) { border-radius: 0; }
.course-preview-copy { padding: 24px; }
.course-preview-copy > span { color: #0c8192; font-size: 12px; font-weight: 700; }
.course-preview-copy h3 { margin: 10px 0 9px; font-size: 24px; }
.course-preview-copy p { margin: 0; color: var(--video-muted); line-height: 1.7; }
.course-meta { margin-top: 18px; display: flex; flex-wrap: wrap; gap: 16px; color: var(--video-muted); font-size: 12px; }
.course-meta span { display: inline-flex; align-items: center; gap: 6px; }
.course-actions button { color: #071b22; background: var(--video-cyan); }
.course-actions a { color: #0c8192; border: 1px solid var(--video-line); }
.vertical-section { color: #fff; background: #070c11; }
.light-heading p { color: rgba(255,255,255,.5); }
.vertical-video-rail { padding-inline: max(48px, calc((100vw - min(1580px, calc(100vw - 96px))) / 2)); display: flex; align-items: center; justify-content: center; gap: 14px; overflow-x: auto; scrollbar-width: none; }
.vertical-video-rail::-webkit-scrollbar { display: none; }
.vertical-video-card { position: relative; flex: 0 0 320px; aspect-ratio: 9/16; overflow: hidden; border-radius: 22px; cursor: pointer; background: #111820; }
.vertical-video-card.square { flex-basis: 430px; aspect-ratio: 1; }
.vertical-video-card > :deep(.demo-video) { position: absolute; inset: 0; }
.vertical-video-card::after { content: ""; position: absolute; z-index: 2; inset: 45% 0 0; background: linear-gradient(transparent, rgba(2,7,11,.86)); pointer-events: none; }
.vertical-video-copy { position: absolute; z-index: 3; left: 18px; right: 18px; bottom: 65px; display: grid; gap: 4px; pointer-events: none; }
.vertical-video-copy span, .vertical-video-copy small { color: rgba(255,255,255,.58); font-size: 11px; }
.vertical-video-copy strong { font-size: 18px; }
.products-section { padding: 145px 0; background: #fff; }
.product-cta { padding-bottom: 64px; display: flex; align-items: end; justify-content: space-between; gap: 42px; }
.product-cta h2 { font-size: clamp(48px, 5.8vw, 90px); }
.product-cta p { margin: 16px 0 0; color: var(--video-muted); }
.product-entry-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
.product-entry-grid > * { min-height: 120px; padding: 21px; display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: 14px; border: 1px solid var(--video-line); border-radius: 16px; background: var(--video-paper); }
.product-entry-grid > button { color: inherit; font: inherit; text-align: left; cursor: pointer; }
.product-entry-grid > a,
.product-entry-grid > button { transition: border-color .2s ease, transform .2s ease, background .2s ease; }
.product-entry-grid > a:hover,
.product-entry-grid > button:hover { border-color: rgba(12,129,146,.36); background: #edf8f9; transform: translateY(-2px); }
.product-entry-grid svg { color: #0c8192; }
.product-entry-grid strong { display: block; margin-bottom: 5px; }
.product-entry-grid small { color: var(--video-muted); }
.video-home-footer { padding: 30px 0; color: rgba(255,255,255,.5); background: #070c11; font-size: 12px; }
.video-home-footer > div { display: flex; justify-content: space-between; gap: 24px; }
@media (max-width: 980px) {
  .video-home-page { --video-shell: calc(100% - 48px); }
  .create-layout { grid-template-columns: 1fr; gap: 40px; }
  .course-preview-grid { grid-template-columns: 1fr; }
  .product-entry-grid { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 700px) {
  .video-home-page { --video-shell: calc(100% - 30px); }
  .video-hero-content { padding-top: 18svh; padding-bottom: 120px; }
  .video-hero-content h1 { font-size: 104px; }
  .video-hero-content p { margin-top: 24px; font-size: 13px; letter-spacing: .07em; }
  .video-section { padding: 96px 0; }
  .video-section-heading { margin-bottom: 32px; align-items: end; gap: 20px; }
  .video-section-heading h2, .create-copy h2 { font-size: 50px; }
  .video-section-heading p { font-size: 12px; line-height: 1.6; }
  .case-rail { padding-left: 15px; }
  .video-case-card, .video-case-card.featured { min-width: 0; flex-basis: 86vw; }
  .case-video-frame { height: 360px; min-height: 0; }
  .video-create-section { padding: 96px 0; }
  .create-preview { height: 400px; }
  .course-video-frame { height: 260px; }
  .course-preview-copy { padding: 20px; }
  .vertical-video-rail { padding-inline: 15px; justify-content: flex-start; }
  .vertical-video-card { flex-basis: 72vw; }
  .vertical-video-card.square { flex-basis: 78vw; }
  .products-section { padding: 96px 0; }
  .product-cta { align-items: flex-start; flex-direction: column; }
  .product-entry-grid { grid-template-columns: 1fr; }
  .video-home-footer > div { flex-direction: column; }
}
@media (prefers-reduced-motion: reduce) {
  .video-button, .create-preview img { transition: none; }
}
</style>
